import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, LinkButton } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { useContinueToNextPathStep } from 'src/hooks/useContinueToNextPathStep';
import useSafePush from 'src/hooks/useSafePush';
import { useCalendarReminderExit } from 'src/apps/LearningSession/EndOfSession/CalendarReminder/useCalendarReminder';
import { PreCheckpointScreen } from '../PreCheckpointScreen';
import { PathSessionType } from '../types';
import * as S from './styles';

const msgs = defineMessages({
  continueLearning: {
    id: 'path_continue_learning_cta',
    defaultMessage: 'Continue learning',
  },
  goBackToPath: {
    id: 'path_go_back_cta',
    defaultMessage: 'Go back to my path',
  },
});

/** Which of the two exits the learner took, so callers can gate interstitials on one. */
export type PathExit = 'continue' | 'back';

export type PathBeforeNavigate = (proceed: () => void, exit: PathExit) => void;

export type PathContinueButtonsProps = {
  languagePairId: number | null;
  pathwayNodeId?: number | null;
  pathwayCheckpointId?: number | null;
  /** When no pathway node/checkpoint id is present, complete the current matching node. */
  sessionType?: PathSessionType | null;
  layout?: 'stacked' | 'inline';
  /** Called with whichever exit action ("Continue learning" or "Go back to my path") the
   * learner picks, letting the caller defer it behind an interstitial instead of running it
   * right away. When omitted, both exits run immediately. */
  onBeforeNavigate?: PathBeforeNavigate;
};

/**
 * Split out so `useSafePush` (and the router context it requires) is only ever mounted for
 * callers that actually opt into deferred navigation - every other caller keeps rendering a
 * plain link, with no new hook/context requirements.
 */
const PathGoBackDeferredButton: FC<{ onBeforeNavigate: PathBeforeNavigate }> = ({
  onBeforeNavigate,
}) => {
  const intl = useIntl();
  const { safePush } = useSafePush();

  return (
    <Button
      buttonType="secondary"
      buttonSize="large"
      wide
      onClick={() => onBeforeNavigate(() => safePush(ROUTES.path), 'back')}
      data-testid="path-go-back-button"
    >
      {intl.formatMessage(msgs.goBackToPath)}
    </Button>
  );
};

export const PathGoBackButton: FC<{ onBeforeNavigate?: PathBeforeNavigate }> = ({
  onBeforeNavigate,
}) => {
  const intl = useIntl();

  if (onBeforeNavigate) {
    return <PathGoBackDeferredButton onBeforeNavigate={onBeforeNavigate} />;
  }

  return (
    <LinkButton
      buttonType="secondary"
      buttonSize="large"
      wide
      href={ROUTES.path}
      data-testid="path-go-back-button"
    >
      {intl.formatMessage(msgs.goBackToPath)}
    </LinkButton>
  );
};

const PathContinueButtonsContent: FC<
  PathContinueButtonsProps & { onBeforeContinue?: (proceed: () => void) => void }
> = ({
  languagePairId,
  pathwayNodeId = null,
  pathwayCheckpointId = null,
  sessionType = null,
  layout = 'stacked',
  onBeforeNavigate,
  onBeforeContinue,
}) => {
  const intl = useIntl();
  const { safePush } = useSafePush();
  const {
    continueToNextStep,
    isResolving,
    pendingCheckpointUrl,
    confirmCheckpoint,
    isLaunchingCheckpoint,
  } = useContinueToNextPathStep({
    languagePairId,
    pathwayNodeId,
    pathwayCheckpointId,
    sessionType,
  });

  if (pendingCheckpointUrl && languagePairId) {
    return (
      <PreCheckpointScreen
        onContinue={confirmCheckpoint}
        isContinuing={isLaunchingCheckpoint}
        onBack={() => safePush(ROUTES.path)}
      />
    );
  }

  const handleContinueClick = () => {
    if (onBeforeNavigate) {
      onBeforeNavigate(continueToNextStep, 'continue');
    } else if (onBeforeContinue) {
      onBeforeContinue(continueToNextStep);
    } else {
      continueToNextStep();
    }
  };

  return (
    <S.ButtonsWrapper $layout={layout}>
      <Button
        buttonType="primary"
        buttonSize="large"
        wide
        loading={isResolving}
        disabled={isResolving}
        onClick={handleContinueClick}
        data-testid="path-continue-learning-button"
      >
        {intl.formatMessage(msgs.continueLearning)}
      </Button>
      <PathGoBackButton onBeforeNavigate={onBeforeNavigate} />
    </S.ButtonsWrapper>
  );
};

const PathContinueButtonsWithReminder: FC<PathContinueButtonsProps> = props => {
  const { beforeExit, interstitial } = useCalendarReminderExit({
    languagePairId: props.languagePairId,
    pathwayNodeId: props.pathwayNodeId,
    enabled: props.pathwayCheckpointId == null && props.pathwayNodeId != null,
  });
  return (
    <>
      <PathContinueButtonsContent {...props} onBeforeContinue={beforeExit} />
      {interstitial}
    </>
  );
};

export const PathContinueButtons: FC<PathContinueButtonsProps> = props =>
  props.onBeforeNavigate ? (
    <PathContinueButtonsContent {...props} />
  ) : (
    <PathContinueButtonsWithReminder {...props} />
  );
